import type {
  ApiIssueLockerKeyResult,
  ApiLockerKeyAssignment,
  ApiLockerKeyDisabledNumber,
  ApiLockerKeyGroup,
  ApiReleaseOverdueLockerKeysResult,
} from '@mfw/common';

import type {
  ApiLockerKeyAssignmentsResponse,
  ApiLockerKeyBoardResponse,
  ApiLockerKeyDisabledNumbersResponse,
  ApiLockerKeyHoldersResponse,
  ApiLockerKeyOverdueCountResponse,
  ApiLockerKeySearchResponse,
  ArchiveLockerKeyGroupArgs,
  CreateLockerKeyGroupArgs,
  DisableLockerKeyNumberArgs,
  EnableLockerKeyNumberArgs,
  FetchLockerKeyAssignmentsArgs,
  FetchLockerKeyBoardArgs,
  FetchLockerKeyDisabledNumbersArgs,
  FetchLockerKeyGroupsArgs,
  FetchLockerKeyHoldersArgs,
  FetchLockerKeyOverdueCountArgs,
  IssueLockerKeyArgs,
  LockerKeyAssignmentsSnapshot,
  LockerKeyBoardSnapshot,
  LockerKeyOverdueCountSnapshot,
  LockerKeySearchSnapshot,
  ReleaseLockerKeyArgs,
  ReleaseOverdueLockerKeysArgs,
  SearchLockerKeysArgs,
  UpdateLockerKeyGroupArgs,
} from './types/lockerKeys.types';

import { get, patch, post } from './axios';

/**
 * A 2xx body without the fields the contract promises. Thrown rather than
 * defaulted: an empty fallback for `groups` is exactly "no setup yet", and a
 * missing `asOf` leaves no instant the rows (and their overdue days) were
 * evaluated at — both lies about the server.
 */
export class LockerKeyProtocolError extends Error {
  constructor(endpoint: string) {
    super(`Locker keys: malformed response from ${endpoint}`);
    this.name = 'LockerKeyProtocolError';
  }
}

const base = (organizationId: string) =>
  `/v2/organizations/${organizationId}/locker-keys`;

const toQueryNumber = (value?: number) =>
  value === undefined ? undefined : String(value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function requireData<T>(endpoint: string, body: unknown) {
  if (!isRecord(body) || body.data === undefined || body.data === null) {
    throw new LockerKeyProtocolError(endpoint);
  }
  return body.data as T;
}

function requirePage<T extends { data: unknown[]; total: number }>(
  endpoint: string,
  body: T | undefined,
) {
  if (
    !body ||
    !Array.isArray(body.data) ||
    typeof body.total !== 'number' ||
    !Number.isFinite(body.total)
  ) {
    throw new LockerKeyProtocolError(endpoint);
  }
  return body;
}

/** Stamps the monotonic receipt time on a response that carries `asOf`. */
export function stampLockerKeyReceipt<T extends { asOf?: string }>(
  endpoint: string,
  body: T,
) {
  if (typeof body.asOf !== 'string' || Number.isNaN(Date.parse(body.asOf))) {
    throw new LockerKeyProtocolError(endpoint);
  }
  return { ...body, asOf: body.asOf, receivedAt: performance.now() };
}

// ── Groups ─────────────────────────────────────────────────────────────────

export const fetchLockerKeyGroups = async ({
  organizationId,
  includeArchived,
}: FetchLockerKeyGroupsArgs) => {
  const endpoint = `${base(organizationId)}/groups`;
  const body = await get<{ data: ApiLockerKeyGroup[] }>(endpoint, {
    query: {
      includeArchived:
        includeArchived === undefined ? undefined : String(includeArchived),
    },
  });
  const groups = requireData<ApiLockerKeyGroup[]>(endpoint, body);
  if (!Array.isArray(groups)) throw new LockerKeyProtocolError(endpoint);
  return groups;
};

export const createLockerKeyGroup = async ({
  organizationId,
  ...dto
}: CreateLockerKeyGroupArgs) => {
  const endpoint = `${base(organizationId)}/groups`;
  const response = await post<ApiLockerKeyGroup>(endpoint, dto);
  return requireData<ApiLockerKeyGroup>(endpoint, response);
};

export const updateLockerKeyGroup = async ({
  organizationId,
  groupId,
  ...dto
}: UpdateLockerKeyGroupArgs) => {
  const endpoint = `${base(organizationId)}/groups/${groupId}`;
  const response = await patch<ApiLockerKeyGroup>(endpoint, dto);
  return requireData<ApiLockerKeyGroup>(endpoint, response);
};

export const archiveLockerKeyGroup = async ({
  organizationId,
  groupId,
}: ArchiveLockerKeyGroupArgs) => {
  const endpoint = `${base(organizationId)}/groups/${groupId}/archive`;
  const response = await post<ApiLockerKeyGroup>(endpoint);
  return requireData<ApiLockerKeyGroup>(endpoint, response);
};

// ── Board / search / holders ───────────────────────────────────────────────

export const fetchLockerKeyBoard = async ({
  organizationId,
  groupId,
  state,
}: FetchLockerKeyBoardArgs): Promise<LockerKeyBoardSnapshot> => {
  const endpoint = `${base(organizationId)}/board`;
  const body = await get<ApiLockerKeyBoardResponse>(endpoint, {
    query: { groupId, state },
  });
  if (!body || !Array.isArray(body.data)) {
    throw new LockerKeyProtocolError(endpoint);
  }
  return stampLockerKeyReceipt(endpoint, body);
};

export const searchLockerKeys = async ({
  organizationId,
  q,
  state,
  offset,
  limit,
}: SearchLockerKeysArgs): Promise<LockerKeySearchSnapshot> => {
  const endpoint = `${base(organizationId)}/search`;
  const body = await get<ApiLockerKeySearchResponse>(endpoint, {
    query: {
      q,
      state,
      offset: toQueryNumber(offset),
      limit: toQueryNumber(limit),
    },
  });
  return stampLockerKeyReceipt(endpoint, requirePage(endpoint, body));
};

export const fetchLockerKeyHolders = async ({
  organizationId,
  q,
  offset,
  limit,
}: FetchLockerKeyHoldersArgs) => {
  const endpoint = `${base(organizationId)}/holders`;
  const body = await get<ApiLockerKeyHoldersResponse>(endpoint, {
    query: { q, offset: toQueryNumber(offset), limit: toQueryNumber(limit) },
  });
  return requirePage(endpoint, body);
};

// ── Assignments ────────────────────────────────────────────────────────────

export const issueLockerKey = async ({
  organizationId,
  ...dto
}: IssueLockerKeyArgs) => {
  const endpoint = `${base(organizationId)}/assignments`;
  const response = await post<ApiIssueLockerKeyResult>(endpoint, dto);
  const result = requireData<ApiIssueLockerKeyResult>(endpoint, response);
  if (!result.assignment || !Array.isArray(result.warnings)) {
    throw new LockerKeyProtocolError(endpoint);
  }
  return result;
};

export const releaseLockerKeyAssignment = async ({
  organizationId,
  assignmentId,
  reason,
}: ReleaseLockerKeyArgs) => {
  const endpoint = `${base(organizationId)}/assignments/${assignmentId}/release`;
  // No body is the one-click release; the server derives its reason.
  const response = reason
    ? await post<ApiLockerKeyAssignment>(endpoint, { reason })
    : await post<ApiLockerKeyAssignment>(endpoint);
  return requireData<ApiLockerKeyAssignment>(endpoint, response);
};

/** The org-wide count „Oslobodi sve jučerašnje” confirms. */
export const fetchLockerKeyOverdueCount = async ({
  organizationId,
}: FetchLockerKeyOverdueCountArgs): Promise<LockerKeyOverdueCountSnapshot> => {
  const endpoint = `${base(organizationId)}/assignments/overdue-count`;
  const body = await get<ApiLockerKeyOverdueCountResponse>(endpoint);
  if (
    !body ||
    !isRecord(body.data) ||
    typeof body.data.count !== 'number' ||
    !Number.isFinite(body.data.count)
  ) {
    throw new LockerKeyProtocolError(endpoint);
  }
  return stampLockerKeyReceipt(endpoint, body);
};

/** Releases every overdue key of the organization as NOT_RETURNED. */
export const releaseOverdueLockerKeys = async ({
  organizationId,
}: ReleaseOverdueLockerKeysArgs) => {
  const endpoint = `${base(organizationId)}/assignments/release-overdue`;
  const response = await post<ApiReleaseOverdueLockerKeysResult>(endpoint);
  const result = requireData<ApiReleaseOverdueLockerKeysResult>(
    endpoint,
    response,
  );
  if (typeof result.released !== 'number') {
    throw new LockerKeyProtocolError(endpoint);
  }
  return result;
};

export const fetchLockerKeyAssignments = async ({
  organizationId,
  groupId,
  keyNumber,
  holderUserId,
  holderProspectId,
  from,
  to,
  offset,
  limit,
}: FetchLockerKeyAssignmentsArgs): Promise<LockerKeyAssignmentsSnapshot> => {
  const endpoint = `${base(organizationId)}/assignments`;
  const body = await get<ApiLockerKeyAssignmentsResponse>(endpoint, {
    query: {
      groupId,
      keyNumber: toQueryNumber(keyNumber),
      holderUserId,
      holderProspectId,
      from,
      to,
      offset: toQueryNumber(offset),
      limit: toQueryNumber(limit),
    },
  });
  return stampLockerKeyReceipt(endpoint, requirePage(endpoint, body));
};

// ── Disabled numbers ───────────────────────────────────────────────────────

export const disableLockerKeyNumber = async ({
  organizationId,
  ...dto
}: DisableLockerKeyNumberArgs) => {
  const endpoint = `${base(organizationId)}/disabled-numbers`;
  const response = await post<ApiLockerKeyDisabledNumber>(endpoint, dto);
  return requireData<ApiLockerKeyDisabledNumber>(endpoint, response);
};

export const fetchLockerKeyDisabledNumbers = async ({
  organizationId,
  groupId,
  keyNumber,
  from,
  to,
  offset,
  limit,
}: FetchLockerKeyDisabledNumbersArgs) => {
  const endpoint = `${base(organizationId)}/disabled-numbers`;
  const body = await get<ApiLockerKeyDisabledNumbersResponse>(endpoint, {
    query: {
      groupId,
      keyNumber: toQueryNumber(keyNumber),
      from,
      to,
      offset: toQueryNumber(offset),
      limit: toQueryNumber(limit),
    },
  });
  return requirePage(endpoint, body);
};

export const enableLockerKeyNumber = async ({
  organizationId,
  disabledNumberId,
}: EnableLockerKeyNumberArgs) => {
  const endpoint = `${base(organizationId)}/disabled-numbers/${disabledNumberId}/enable`;
  const response = await post<ApiLockerKeyDisabledNumber>(endpoint);
  return requireData<ApiLockerKeyDisabledNumber>(endpoint, response);
};
